import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionUserEvent,
} from '../../types/ServiceEvents';
import requireUser, { rosterUser } from '../../lib/requireUser';
import log from '../../lib/log';
import isValidUser from '../../lib/isValidUser';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import {
  applyAgentSessionNow,
  enqueueNonSelfAgentSessionEvent,
  shouldBufferNonSelfAgentSession,
} from '../../lib/coalesceWorkspaceEvents';

/**
 * The rescind event indicates that an offer has been rescinded from
 * the recipient. This could happen for example if a session hung-up
 * while it was being offered.
 * @param event
 */
async function prepareAgentSessionRescinded(
  event: Telecom.AgentSessionRescindedEvent,
) {
  if (!isValidUser(event.agentId)) {
    return undefined;
  }

  const user = await requireUser(event.agentId);
  return { user };
}

function commitAgentSessionRescinded(
  event: Telecom.AgentSessionRescindedEvent,
  prepared: NonNullable<
    Awaited<ReturnType<typeof prepareAgentSessionRescinded>>
  >,
) {
  const user = rosterUser(event.agentId, prepared.user);
  log('rtcs:agent:session_rescinded:' + user?.name, event);

  user.stats = userStatsFromAgentStats(event.stats);

  ServiceUserUtils.setEventActivityStatus(user, '');
  appManager.events.emit<ServiceSessionUserEvent>(
    ServiceSessionEventType.RESCINDED,
    {
      sessionId: event.conversationId ?? '',
      sessionSid: event.conversationSid ?? '',
      agentId: event.agentId,
      userId: user.id,
    },
  );
}

async function handleAgentSessionRescinded(
  event: Telecom.AgentSessionRescindedEvent,
) {
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_RESCINDED);
  if (!shouldBufferNonSelfAgentSession(event.agentId)) {
    return applyAgentSessionNow(event.agentId, (user) => {
      commitAgentSessionRescinded(event, { user });
    });
  }
  return enqueueNonSelfAgentSessionEvent(event.agentId, {
    prepare: () => prepareAgentSessionRescinded(event),
    commit: (value) => commitAgentSessionRescinded(event, value),
  });
}

export default handleAgentSessionRescinded;
